// src/modulos/Parametros/RolesPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const ROLES_COLLECTION = 'parameters/data/cargos';
const ITEMS_PER_PAGE = 10;

const RolesPage = () => {
    const can = usePagePermissions('cargos');

    // Estados Locais
    const [editingRole, setEditingRole] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({
        isOpen: false,
        id: null,
        name: ''
    });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: roles,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData,
    } = useFirestore(ROLES_COLLECTION, 'name');

    // Filtro e Paginação
    const filteredRoles = useMemo(() => {
        if (!roles) return [];
        if (!searchTerm) return roles;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return roles.filter((role) =>
            role.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [roles, searchTerm]);

    const totalFilteredItems = filteredRoles.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentRoles = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredRoles.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredRoles, currentPage]);

    // Reset do Formulário
    const resetForm = useCallback(() => {
        setEditingRole(null);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((role) => {
        setEditingRole(role);
        setNameInput(role.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    // Validação de Duplicados
    const checkDuplicateRole = useCallback(
        (name, excludeId = null) => {
            const normalizedName = name.trim().toLowerCase();
            return roles?.some(
                (role) =>
                    role.name.trim().toLowerCase() === normalizedName &&
                    role.id !== excludeId
            );
        },
        [roles]
    );

    // Submissão do Formulário
    const handleFormSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormError('');
            setFormSuccess('');

            const trimmedName = nameInput.trim();

            if (!trimmedName) {
                setFormError('O nome do Cargo é obrigatório.');
                return;
            }

            if (checkDuplicateRole(trimmedName, editingRole?.id)) {
                setFormError(
                    `O cargo "${trimmedName}" já existe. Por favor, utilize um nome diferente.`
                );
                return;
            }

            const userEmail = localStorage.getItem('userEmail') || 'system';
            const timestamps = getTimestamps(editingRole, userEmail);
            const data = {
                name: trimmedName,
                ...timestamps,
            };

            setFormLoading(true);

            try {
                let result;
                if (editingRole) {
                    result = await updateDocument(editingRole.id, data);
                } else {
                    result = await addDocument(data);
                }

                if (result.success) {
                    setFormSuccess(
                        `Cargo ${editingRole ? 'editado' : 'adicionado'} com sucesso!`
                    );
                    setTimeout(() => {
                        handleCloseModal();
                    }, 1500);
                } else {
                    setFormError(
                        `Falha ao ${editingRole ? 'editar' : 'adicionar'}: ${result.error}`
                    );
                }
            } catch (error) {
                setFormError(`Erro inesperado: ${error.message}`);
            } finally {
                setFormLoading(false);
            }
        },
        [nameInput, editingRole, checkDuplicateRole, addDocument, updateDocument, handleCloseModal]
    );

    // Lógica para Deletar com verificação de uso
    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            // Importações dinâmicas para verificação de uso
            const { getDocs, collection, query, where } = await import('firebase/firestore');
            const { db } = await import('../../firebaseConfig');

            const usersCollection = collection(db, 'users');

            // Verificar associações com usuários
            let q = query(usersCollection, where('roleId', '==', id));
            let querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                q = query(usersCollection, where('cargo', '==', name));
                querySnapshot = await getDocs(q);
            }

            if (querySnapshot.empty) {
                q = query(usersCollection, where('role', '==', name));
                querySnapshot = await getDocs(q);
            }

            if (!querySnapshot.empty) {
                setFormError(
                    `Não é possível deletar o cargo "${name}". Existem ${querySnapshot.size} usuário(s) associado(s) a este cargo.`
                );
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Cargo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Ajustar página após exclusão
                const remainingItems = filteredRoles.length - 1;
                if (
                    currentPage > 1 &&
                    remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE
                ) {
                    setCurrentPage((prev) => prev - 1);
                }
            } else {
                setFormError('Erro ao deletar Cargo: ' + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
            }
        } catch (err) {
            console.error('Erro ao verificar associações do cargo:', err);
            setFormError('Erro ao verificar se o cargo está em uso. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredRoles.length, currentPage]);

    // Paginação
    const handlePageChange = useCallback(
        (newPage) => {
            if (newPage > 0 && newPage <= totalPagesAfterFilter) {
                setCurrentPage(newPage);
            }
        },
        [totalPagesAfterFilter]
    );

    // Resetar página quando o termo de busca mudar
    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingRole ? `Editar: ${editingRole.name}` : 'Adicionar Novo Cargo'}
                subtitle={editingRole ? `Editando: ${editingRole.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingRole ? '💾 Salvar Edição' : '✨ Adicionar Cargo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Cargo <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Nome do Cargo (ex: Gerente de Vendas)"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome único que identifique a função
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o cargo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Cargos"
                actionText="Adicionar Cargo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome de cargo..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentRoles.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '👔'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum cargo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? 'Tente usar outro termo ou limpe a busca.'
                                    : "Clique em 'Adicionar Cargo' para começar."
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Cargo</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRoles.map((role) => (
                                            <tr key={role.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{role.name}</strong>
                                                    {role.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Última atualização:{' '}
                                                            {new Date(role.updatedAt).toLocaleDateString(
                                                                'pt-BR'
                                                            )}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(role)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() =>
                                                                handleDeleteClick(role.id, role.name)
                                                            }
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="cargos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default RolesPage;